import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, getToken } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  Sparkles,
  Search,
  Play,
  Loader2,
  Upload,
  Trash2,
  Film,
  FileVideo,
  X,
  Cloud,
  CloudOff,
  CloudUpload,
  CheckCircle2,
  AlertTriangle,
  RefreshCw,
  Zap,
  Music,
  Scissors,
  Tags,
  CalendarPlus,
  RotateCcw,
  History,
  Pencil,
  Clapperboard,
  CopyCheck,
  Copy,
  MoreVertical,
} from "lucide-react";
import { toast } from "sonner";
import AssetPreviewPlayer from "@/components/AssetPreviewPlayer";
import SocialClipsDialog from "@/components/SocialClipsDialog";
import R2StoragePanel from "./assets/R2StoragePanel";

const TYPES = ["all", "program", "promo", "ad", "bumper", "slate"];
const UPLOAD_TYPES = ["program", "promo", "ad", "bumper", "slate"];
const MAX_UPLOAD_MB = 500;

function fmtDur(sec) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  return h > 0 ? `${h}h ${m}m` : `${m}m ${s}s`;
}

function fmtSpeed(bytesPerSec) {
  if (!bytesPerSec || bytesPerSec <= 0) return "";
  const mb = bytesPerSec / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB/s`;
  return `${(bytesPerSec / 1024).toFixed(0)} KB/s`;
}

function fmtEta(sec) {
  if (sec == null || !isFinite(sec) || sec <= 0) return "";
  const s = Math.ceil(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
}

// Resume-after-refresh: remember in-flight chunked upload ids keyed by a file
// signature (name:size:lastModified) so re-selecting the same file after a
// reload continues from the server's next_index instead of restarting.
const UPLOAD_LS_KEY = "mp_resumable_uploads";
function fileSig(file) {
  return `${file.name}:${file.size}:${file.lastModified}`;
}
function loadResumableMap() {
  try { return JSON.parse(localStorage.getItem(UPLOAD_LS_KEY) || "{}"); } catch { return {}; }
}
function rememberResumable(sig, uploadId) {
  const m = loadResumableMap();
  if (uploadId) m[sig] = { upload_id: uploadId, ts: Date.now() };
  else delete m[sig];
  try { localStorage.setItem(UPLOAD_LS_KEY, JSON.stringify(m)); } catch { /* quota */ }
}

function fmtClock(sec) {
  const s = Math.max(0, Number(sec) || 0);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = Math.floor(s % 60);
  const mm = String(m).padStart(2, "0");
  const rr = String(r).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${rr}` : `${mm}:${rr}`;
}

function fmtSize(bytes) {
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

const withAuth = (url) => {
  if (!url || !url.startsWith("/api/")) return url;
  const token = getToken() || "";
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}auth=${encodeURIComponent(token)}`;
};

function UploadDialog({ open, onClose, onUploaded }) {
  // Each queue item: { key, file, name, status: "pending" | "uploading" | "processing" | "done" | "error", progress, error, asset }
  const [queue, setQueue] = useState([]);
  const [type, setType] = useState("program");
  const [tags, setTags] = useState("");
  const [autoClassify, setAutoClassify] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [running, setRunning] = useState(false);
  const inputRef = useRef(null);

  const busyQueue = queue.some((q) => q.status === "uploading" || q.status === "processing" || q.status === "classifying");
  const canRunMore = queue.some((q) => q.status === "pending");
  const doneCount = queue.filter((q) => q.status === "done").length;
  const errCount = queue.filter((q) => q.status === "error").length;
  const totalCount = queue.length;

  useEffect(() => {
    if (!open) {
      setQueue([]); setType("program"); setTags(""); setRunning(false);
    }
  }, [open]);

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const accepted = [];
    for (const f of files) {
      if (!f.type.startsWith("video/")) {
        toast.error(`${f.name}: not a video file`);
        continue;
      }
      if (f.size > MAX_UPLOAD_MB * 1024 * 1024) {
        toast.error(`${f.name}: too large (max ${MAX_UPLOAD_MB} MB)`);
        continue;
      }
      accepted.push({
        key: `${f.name}-${f.size}-${f.lastModified}-${Math.random().toString(36).slice(2, 6)}`,
        file: f,
        name: f.name.replace(/\.[^.]+$/, ""),
        status: "pending",
        progress: 0,
      });
    }
    if (accepted.length) {
      setQueue((prev) => [...prev, ...accepted]);
      toast.success(`Queued ${accepted.length} file${accepted.length === 1 ? "" : "s"}`);
    }
  };

  const uploadChunked = async (item, startedAt) => {
    const file = item.file;
    const ctype = file.type || "video/mp4";
    const sig = fileSig(file);
    let uploadId = null;
    let chunkSize = 8 * 1024 * 1024;
    let index = 0;
    let sent = 0;
    // Try to resume a prior interrupted upload for this exact file.
    const saved = loadResumableMap()[sig];
    if (saved?.upload_id) {
      try {
        const st = await api.get(`/assets/upload/${saved.upload_id}/status`);
        if (st.data.status === "open" && st.data.total_size === file.size) {
          uploadId = saved.upload_id;
          chunkSize = st.data.chunk_size || chunkSize;
          index = st.data.next_index || 0;
          sent = st.data.received_bytes || 0;
          if (sent > 0) toast.info(`Resuming "${file.name}" from ${Math.round((sent / file.size) * 100)}%`);
        }
      } catch (_) { /* stale/expired — start fresh below */ }
    }
    if (!uploadId) {
      const init = await api.post("/assets/upload/init", {
        filename: file.name,
        content_type: ctype,
        total_size: file.size,
        name: item.name || undefined,
        type,
        tags: tags || undefined,
      });
      uploadId = init.data.upload_id;
      chunkSize = init.data.chunk_size || chunkSize;
      index = init.data.next_index || 0;
      sent = index * chunkSize;
    }
    rememberResumable(sig, uploadId);
    setQueue((prev) => prev.map((q) => q.key === item.key ? { ...q, uploadId } : q));
    const totalChunks = Math.ceil(file.size / chunkSize);
    while (index < totalChunks) {
      const start = index * chunkSize;
      const blob = file.slice(start, Math.min(file.size, start + chunkSize));
      let attempt = 0;
      // Per-chunk retry with backoff — survives a dropped connection or a
      // mid-flight pod restart (we resync next_index from the server, whose
      // chunk store is durable, then keep going).
      for (;;) {
        try {
          const r = await api.put(`/assets/upload/${uploadId}/chunk?index=${index}`, blob, {
            headers: { "Content-Type": "application/octet-stream" },
            timeout: 120000,
          });
          sent = r.data.received_bytes;
          index = r.data.next_index;
          break;
        } catch (err) {
          attempt += 1;
          if (attempt >= 5) throw err;
          try {
            const st = await api.get(`/assets/upload/${uploadId}/status`);
            index = st.data.next_index;
            sent = st.data.received_bytes;
          } catch (_) { /* keep last known index */ }
          await new Promise((res) => setTimeout(res, 600 * attempt));
        }
      }
      const pct = Math.round((sent / file.size) * 100);
      const elapsed = (Date.now() - startedAt) / 1000;
      const speed = elapsed > 0 ? sent / elapsed : 0;
      const eta = speed > 0 ? (file.size - sent) / speed : null;
      setQueue((prev) => prev.map((q) => q.key === item.key
        ? { ...q, progress: pct, speed, eta, status: pct >= 100 ? "processing" : "uploading" }
        : q));
    }
    const r = await api.post(`/assets/upload/${uploadId}/complete`, {}, { timeout: 600000 });
    rememberResumable(sig, null);  // clear on success
    return r.data;
  };

  const uploadOne = async (item) => {
    setQueue((prev) => prev.map((q) => q.key === item.key ? { ...q, status: "uploading", progress: 0, speed: 0, eta: null } : q));
    const startedAt = Date.now();
    try {
      let asset;
      // Large files → resumable chunked upload (bounded memory, retry/resume).
      // Small files keep the single-shot multipart path.
      if (item.file.size > 8 * 1024 * 1024) {
        asset = await uploadChunked(item, startedAt);
        setQueue((prev) => prev.map((q) => q.key === item.key ? { ...q, status: "processing", progress: 100 } : q));
      } else {
        const form = new FormData();
        form.append("file", item.file);
        form.append("type", type);
        if (item.name) form.append("name", item.name);
        if (tags) form.append("tags", tags);
        const r = await api.post("/assets/upload", form, {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (!e.total) return;
            const pct = Math.round((e.loaded / e.total) * 100);
            const elapsed = (Date.now() - startedAt) / 1000;
            const speed = elapsed > 0 ? e.loaded / elapsed : 0;
            const eta = speed > 0 ? (e.total - e.loaded) / speed : null;
            setQueue((prev) => prev.map((q) =>
              q.key === item.key
                ? { ...q, progress: pct, speed, eta, status: pct >= 100 ? "processing" : "uploading" }
                : q
            ));
          },
          timeout: 600000,  // 10 min per file
        });
        asset = r.data;
      }
      setQueue((prev) => prev.map((q) => q.key === item.key ? { ...q, status: "done", progress: 100, asset } : q));
      onUploaded(asset);
      if (autoClassify) {
        // Fire-and-forget AI metadata generation — updates queue + parent list on completion
        setQueue((prev) => prev.map((q) => q.key === item.key ? { ...q, status: "classifying" } : q));
        try {
          const ai = await api.post(`/ai/metadata/${asset.id}`);
          const enriched = { ...asset, ai_metadata: ai.data.ai_metadata };
          setQueue((prev) => prev.map((q) => q.key === item.key ? { ...q, asset: enriched } : q));
          onUploaded(enriched);
          // Fuzzy-match the AI genre_tags against the tenant's music categories
          // so the uploaded track drops into the right slots automatically.
          try {
            const tagRes = await api.post(`/assets/${asset.id}/auto-tag-categories`);
            const matchedNames = tagRes.data.matched_names || [];
            const finalAsset = { ...enriched, category_ids: tagRes.data.category_ids || [] };
            setQueue((prev) => prev.map((q) => q.key === item.key
              ? { ...q, status: "done", asset: finalAsset, matchedCategories: matchedNames }
              : q));
            onUploaded(finalAsset);
          } catch {
            setQueue((prev) => prev.map((q) => q.key === item.key ? { ...q, status: "done" } : q));
          }
        } catch {
          setQueue((prev) => prev.map((q) => q.key === item.key ? { ...q, status: "done" } : q));
        }
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Upload failed";
      setQueue((prev) => prev.map((q) => q.key === item.key ? { ...q, status: "error", error: msg } : q));
    }
  };

  const runAll = async () => {
    setRunning(true);
    const CONCURRENCY = 3;
    // Snapshot pending items at start; use a mutable pointer
    let pending = queue.filter((q) => q.status === "pending").map((q) => q.key);
    const inFlight = new Set();

    while (pending.length > 0 || inFlight.size > 0) {
      // Fill up to CONCURRENCY
      while (inFlight.size < CONCURRENCY && pending.length > 0) {
        const key = pending.shift();
        const item = queue.find((q) => q.key === key);
        if (!item) continue;
        const p = uploadOne(item).finally(() => inFlight.delete(p));
        inFlight.add(p);
      }
      // Wait for at least one to finish
      if (inFlight.size > 0) await Promise.race(inFlight);
    }
    setRunning(false);
    const finalDone = queue.filter((q) => q.status === "done").length;
    if (finalDone > 0 || doneCount > 0) {
      toast.success(`Upload complete`);
    }
  };

  const removeItem = (key) => {
    setQueue((prev) => prev.filter((q) => q.key !== key));
  };

  const clearFinished = () => {
    setQueue((prev) => prev.filter((q) => q.status === "pending" || q.status === "uploading" || q.status === "processing"));
  };

  const closeDialog = () => {
    if (busyQueue) return;
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && closeDialog()}>
      <DialogContent className="bg-card border-border rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="upload-dialog">
        <DialogHeader className="relative pb-4 mb-1">
          <div className="absolute inset-x-0 -top-6 -mx-6 h-1" style={{ background: "var(--brand-gradient)" }} />
          <DialogTitle className="font-display flex items-center gap-2">
            <FileVideo className="w-5 h-5 text-fuchsia-400" strokeWidth={1.75} />
            Upload media
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground font-mono">
            Drop multiple videos to batch upload · MP4 / MOV / MKV / WEBM · up to {MAX_UPLOAD_MB} MB each · up to 3 uploads in parallel.
          </DialogDescription>
        </DialogHeader>

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault(); setDragOver(false);
            addFiles(e.dataTransfer.files);
          }}
          onClick={() => !busyQueue && inputRef.current?.click()}
          data-testid="upload-dropzone"
          className={`rounded-2xl border-2 border-dashed p-6 cursor-pointer transition-colors ${
            dragOver ? "border-fuchsia-400 bg-fuchsia-500/5" : "border-border hover:border-fuchsia-400/60"
          } ${busyQueue ? "opacity-50 pointer-events-none" : ""}`}
        >
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="video/mp4,video/quicktime,video/x-matroska,video/webm"
            className="hidden"
            data-testid="upload-file-input"
            onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
          />
          <div className="text-center">
            <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" strokeWidth={1.5} />
            <div className="font-display font-bold text-base">
              {queue.length === 0 ? "Drop videos, or click to browse" : "Drop more videos, or click to add"}
            </div>
            <div className="text-xs text-muted-foreground mt-1">MP4 · MOV · MKV · WEBM · max {MAX_UPLOAD_MB} MB each</div>
          </div>
        </div>

        {/* Shared metadata (applied to all files) */}
        {queue.length > 0 && (
          <div className="pt-3 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Type (all files)</Label>
                <select
                  value={type}
                  onChange={(e) => setType(e.target.value)}
                  data-testid="upload-type-select"
                  disabled={busyQueue}
                  className="mt-1 w-full h-10 rounded-sm bg-background border border-border font-mono text-xs px-2 uppercase tracking-widest"
                >
                  {UPLOAD_TYPES.map((t) => (<option key={t} value={t}>{t}</option>))}
                </select>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Tags (all files, optional)</Label>
                <Input
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  className="mt-1 bg-background border-border font-mono text-xs"
                  data-testid="upload-tags-input"
                  disabled={busyQueue}
                  placeholder="e.g. drama, 2024"
                />
              </div>
            </div>
            <label
              className={`flex items-start gap-2 gradient-stroke rounded-xl p-3 cursor-pointer ${busyQueue ? "opacity-60 pointer-events-none" : "hover:brightness-105"}`}
              data-testid="upload-autoclassify-toggle"
            >
              <input
                type="checkbox"
                checked={autoClassify}
                onChange={(e) => setAutoClassify(e.target.checked)}
                disabled={busyQueue}
                className="mt-0.5 accent-fuchsia-500"
              />
              <div className="flex-1">
                <div className="flex items-center gap-1.5 font-display font-bold text-sm">
                  <Sparkles className="w-3.5 h-3.5 text-fuchsia-400" strokeWidth={1.75} />
                  Auto-classify with AI + tag categories after upload
                </div>
                <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
                  Runs Claude/Gemini on each file&apos;s name + type to generate genre tags & synopsis, then fuzzy-matches those tags against your music categories and drops each track into the right slots. Uses your Emergent LLM key.
                </div>
              </div>
            </label>
          </div>
        )}

        {/* Queue */}
        {queue.length > 0 && (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                Queue · {totalCount} file{totalCount === 1 ? "" : "s"} · {doneCount} done · {errCount} failed
              </div>
              {!busyQueue && (doneCount > 0 || errCount > 0) && (
                <button
                  onClick={clearFinished}
                  data-testid="upload-clear-finished"
                  className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
                >
                  Clear finished
                </button>
              )}
            </div>
            <div className="space-y-1.5 max-h-[280px] overflow-y-auto scrollbar-thin pr-1">
              {queue.map((q) => {
                const statusColor = {
                  pending: "text-muted-foreground",
                  uploading: "text-cyan-500 dark:text-cyan-300",
                  processing: "text-fuchsia-500 dark:text-fuchsia-300",
                  classifying: "text-fuchsia-500 dark:text-fuchsia-300",
                  done: "text-emerald-500 dark:text-emerald-400",
                  error: "text-rose-500 dark:text-rose-400",
                }[q.status];
                const statusLabel = {
                  pending: "Queued",
                  uploading: `${q.progress}%${q.speed ? ` · ${fmtSpeed(q.speed)}` : ""}${(q.eta != null && q.progress < 100 && fmtEta(q.eta)) ? ` · ${fmtEta(q.eta)} left` : ""}`,
                  processing: "Processing…",
                  classifying: "AI-classify…",
                  done: q.asset?.ai_metadata?.genre_tags?.length
                    ? `${q.asset.resolution} · ${(q.asset.ai_metadata.genre_tags || []).slice(0,3).join(", ")}`
                    : q.asset ? `${q.asset.resolution} · ${fmtDur(q.asset.duration_sec)}` : "Done",
                  error: "Failed",
                }[q.status];
                return (
                  <div key={q.key} data-testid={`queue-item-${q.key}`} className="panel rounded-md p-2.5 flex items-center gap-3">
                    <Film className="w-4 h-4 text-muted-foreground shrink-0" strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="font-mono text-xs truncate">{q.name}</div>
                        <div className={`text-[10px] font-mono uppercase tracking-widest ${statusColor} shrink-0`}>
                          {statusLabel}
                        </div>
                      </div>
                      <div className="mt-1 w-full h-0.5 bg-accent rounded-full overflow-hidden">
                        <div
                          className="h-full transition-all duration-300"
                          style={{
                            width: `${q.status === "done" ? 100 : q.status === "error" ? 100 : q.progress}%`,
                            background: q.status === "error" ? "rgb(244,63,94)" : q.status === "done" ? "rgb(16,185,129)" : "var(--brand-gradient)",
                          }}
                        />
                      </div>
                      {q.status === "error" && q.error && (
                        <div className="text-[10px] text-rose-400 font-mono mt-0.5 truncate">{q.error}</div>
                      )}
                      {q.matchedCategories && q.matchedCategories.length > 0 && (
                        <div
                          className="text-[10px] mt-1 flex items-center gap-1 flex-wrap"
                          data-testid={`queue-matched-${q.key}`}
                        >
                          <span className="text-emerald-500 font-medium">Auto-tagged:</span>
                          {q.matchedCategories.slice(0, 4).map((n) => (
                            <span key={n} className="px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-500 border border-emerald-500/40 font-mono uppercase text-[9px]">
                              {n}
                            </span>
                          ))}
                          {q.matchedCategories.length > 4 && (
                            <span className="text-muted-foreground">+{q.matchedCategories.length - 4}</span>
                          )}
                        </div>
                      )}
                      {q.matchedCategories !== undefined && q.matchedCategories.length === 0 && (
                        <div className="text-[10px] mt-0.5 text-amber-500/80" data-testid={`queue-no-match-${q.key}`}>
                          No category matched — assign manually
                        </div>
                      )}
                    </div>
                    {q.status === "pending" && !running && (
                      <button
                        onClick={() => removeItem(q.key)}
                        data-testid={`queue-remove-${q.key}`}
                        className="p-1 rounded-sm hover:bg-accent text-muted-foreground hover:text-rose-500"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    )}
                    {q.status === "done" && (
                      <div className="w-6 h-6 rounded-full bg-emerald-500/15 flex items-center justify-center text-emerald-500 dark:text-emerald-400 shrink-0">
                        ✓
                      </div>
                    )}
                    {q.status === "error" && (
                      <div className="w-6 h-6 rounded-full bg-rose-500/15 flex items-center justify-center text-rose-500 dark:text-rose-400 shrink-0">
                        !
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <DialogFooter className="pt-4">
          <Button variant="outline" onClick={closeDialog} disabled={busyQueue} className="rounded-sm border-border">
            {busyQueue ? "Uploading…" : (doneCount > 0 || errCount > 0) ? "Close" : "Cancel"}
          </Button>
          <Button
            onClick={runAll}
            disabled={!canRunMore || busyQueue}
            data-testid="upload-submit-btn"
            className="rounded-sm tenant-btn"
          >
            {busyQueue ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Uploading {queue.filter((q) => q.status === "uploading" || q.status === "processing").length}…</> :
              <><Upload className="w-4 h-4 mr-2" /> Start upload ({queue.filter((q) => q.status === "pending").length})</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CdnPill({ asset, onRetry, busy }) {
  const s = asset.cdn_status || "none";
  // R2-backed asset — served as a direct MP4 from Cloudflare R2.
  if (asset.r2_url || asset.cdn_provider === "r2") {
    return (
      <Badge
        className="bg-cyan-500/15 text-cyan-600 dark:text-cyan-300 border border-cyan-400/40 text-[9px] uppercase font-mono flex items-center gap-1"
        data-testid={`asset-cdn-pill-${asset.id}`}
        title={asset.r2_url || "Cloudflare R2"}
      >
        <CheckCircle2 className="w-2.5 h-2.5" /> R2 ready
      </Badge>
    );
  }
  if (s === "ready") {
    return (
      <Badge
        className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-400/40 text-[9px] uppercase font-mono flex items-center gap-1"
        data-testid={`asset-cdn-pill-${asset.id}`}
        title={asset.cloudflare_hls_url || "Cloudflare Stream"}
      >
        <CheckCircle2 className="w-2.5 h-2.5" /> CDN ready
      </Badge>
    );
  }
  if (s === "queued" || s === "publishing") {
    return (
      <Badge
        className="bg-cyan-500/15 text-cyan-600 dark:text-cyan-300 border border-cyan-400/40 text-[9px] uppercase font-mono flex items-center gap-1"
        data-testid={`asset-cdn-pill-${asset.id}`}
      >
        <Loader2 className="w-2.5 h-2.5 animate-spin" /> CDN {s}
      </Badge>
    );
  }
  if (s === "failed") {
    return (
      <div className="flex items-center gap-1">
        <Badge
          className="bg-rose-500/15 text-rose-600 dark:text-rose-300 border border-rose-400/40 text-[9px] uppercase font-mono flex items-center gap-1"
          data-testid={`asset-cdn-pill-${asset.id}`}
          title={asset.cdn_error || "CDN publish failed"}
        >
          <AlertTriangle className="w-2.5 h-2.5" /> CDN failed
        </Badge>
        {onRetry && (
          <button
            type="button"
            onClick={() => onRetry(asset.id)}
            disabled={busy}
            data-testid={`asset-cdn-retry-${asset.id}`}
            className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-fuchsia-500/40 text-fuchsia-500 hover:bg-fuchsia-500/15 disabled:opacity-50"
            title={`Retry publish · last error: ${asset.cdn_error || "unknown"}`}
          >
            {busy ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : "Retry"}
          </button>
        )}
      </div>
    );
  }
  return (
    <Badge
      className="bg-muted/40 text-muted-foreground border border-border text-[9px] uppercase font-mono flex items-center gap-1"
      data-testid={`asset-cdn-pill-${asset.id}`}
    >
      <CloudOff className="w-2.5 h-2.5" /> No CDN
    </Badge>
  );
}


export default function Assets() {
  const { user } = useAuth();
  const canWrite = user && ["super_admin", "tenant_admin", "scheduler"].includes(user.role);
  const [assets, setAssets] = useState([]);
  const [groupDupes, setGroupDupes] = useState(true);
  const [sortKey, setSortKey] = useState("default"); // default | name | artist | year | upload
  const [r2Open, setR2Open] = useState(false);

  // Duplicate detection: exact re-uploads share the same name + duration. Group
  // them so the library stays tidy as copies pile up.
  const dupKey = (a) =>
    `${(a.name || "").trim().toLowerCase()}|${Math.round(a.effective_duration_sec || a.duration_sec || 0)}`;
  const { orderedAssets, dupCounts, dupIndex } = useMemo(() => {
    const counts = {};
    assets.forEach((a) => { const k = dupKey(a); counts[k] = (counts[k] || 0) + 1; });
    // Explicit sort (artist / year / newest upload / name) takes precedence
    // over duplicate-clustering so large libraries are easy to scan.
    if (sortKey !== "default") {
      const cmp = {
        name: (x, y) => (x.name || "").localeCompare(y.name || ""),
        artist: (x, y) => (x.artist_name || "").localeCompare(y.artist_name || "") || (x.name || "").localeCompare(y.name || ""),
        year: (x, y) => (y.release_year || 0) - (x.release_year || 0),
        upload: (x, y) => String(y.created_at || "").localeCompare(String(x.created_at || "")),
      }[sortKey];
      return { orderedAssets: [...assets].sort(cmp), dupCounts: counts, dupIndex: {} };
    }
    if (!groupDupes) return { orderedAssets: assets, dupCounts: counts, dupIndex: {} };
    const firstSeen = {};
    assets.forEach((a, i) => { const k = dupKey(a); if (firstSeen[k] === undefined) firstSeen[k] = i; });
    const ordered = [...assets].sort((x, y) => {
      const kx = dupKey(x), ky = dupKey(y);
      const dx = counts[kx] > 1, dy = counts[ky] > 1;
      if (dx !== dy) return dx ? -1 : 1;                 // duplicate groups float to the top
      if (dx && kx !== ky) return firstSeen[kx] - firstSeen[ky]; // keep each group clustered
      return 0;                                          // stable: preserve order within group / singles
    });
    const idx = {}; const seen = {};
    ordered.forEach((a) => { const k = dupKey(a); if (counts[k] > 1) { seen[k] = (seen[k] || 0) + 1; idx[a.id] = seen[k]; } });
    return { orderedAssets: ordered, dupCounts: counts, dupIndex: idx };
  }, [assets, groupDupes, sortKey]);
  const dupGroupCount = useMemo(
    () => Object.values(dupCounts).filter((n) => n > 1).length, [dupCounts]);
  const [q, setQ] = useState("");
  const [catFilter, setCatFilter] = useState("all"); // view songs in one category
  const [fullLengthOnly, setFullLengthOnly] = useState(false); // songs with no OUT point
  const [searchParams] = useSearchParams();
  const [type, setType] = useState(() => {
    try { return localStorage.getItem("mp_asset_type_filter") || "all"; } catch { return "all"; }
  });
  const [busyId, setBusyId] = useState(null);
  const [cdnBusyId, setCdnBusyId] = useState(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [regen, setRegen] = useState(null);
  const [previewAsset, setPreviewAsset] = useState(null);
  const [clipAsset, setClipAsset] = useState(null);
  const [cdnEnabled, setCdnEnabled] = useState(false);
  const [r2Enabled, setR2Enabled] = useState(false);
  const [allCategories, setAllCategories] = useState([]);
  const [channelsList, setChannelsList] = useState([]);
  const [assignAsset, setAssignAsset] = useState(null); // asset being tagged
  const [editAsset, setEditAsset] = useState(null); // asset whose title/artist/year is being edited
  // Re-upload watchlist — every asset row whose `storage_path` is null.
  // Loaded from /cloudflare-stream/missing-source. Shown as a compact
  // sticky panel above the grid so nothing else silently rots.
  const [missingSource, setMissingSource] = useState([]);
  const [missingCollapsed, setMissingCollapsed] = useState(false);
  const [trashSummary, setTrashSummary] = useState({ count: 0, expiring_soon: 0, warn_days: 7 });
  const [trashAlertDismissed, setTrashAlertDismissed] = useState(false);

  const load = () => {
    const params = {};
    if (type !== "all") params.type = type;
    if (q) params.q = q;
    if (catFilter !== "all") params.category_id = catFilter;
    if (fullLengthOnly) params.full_length = true;
    api.get("/assets", { params }).then((r) => setAssets(r.data)).catch(() => toast.error("Failed to load assets"));
  };
  const loadTrashSummary = () => {
    api.get("/assets/trash/summary").then((r) => setTrashSummary(r.data || {})).catch(() => { /* noop */ });
  };

  useEffect(() => {
    // Live-type search + instant filters, debounced so typing doesn't spam the API.
    const t = setTimeout(() => { load(); }, 300);
    return () => clearTimeout(t);
  }, [type, catFilter, q]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [fullLengthOnly]);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { try { localStorage.setItem("mp_asset_type_filter", type); } catch { /* quota */ } }, [type]);
  // Category Quick-View: open pre-filtered to a category via ?category=<id>.
  useEffect(() => {
    const cat = searchParams.get("category");
    if (cat) setCatFilter(cat);
  }, [searchParams]);

  useEffect(() => {
    api.get("/cloudflare-stream/status")
      .then((r) => setCdnEnabled(!!r.data?.enabled))
      .catch(() => setCdnEnabled(false));
    api.get("/r2/status")
      .then((r) => setR2Enabled(!!r.data?.enabled))
      .catch(() => setR2Enabled(false));
    api.get("/categories")
      .then((r) => setAllCategories(r.data))
      .catch(() => { /* noop */ });
    api.get("/channels")
      .then((r) => setChannelsList(r.data))
      .catch(() => { /* noop */ });
    api.get("/cloudflare-stream/missing-source")
      .then((r) => setMissingSource(r.data?.assets || []))
      .catch(() => { /* noop */ });
  }, []);

  // Refresh missing-source list whenever the asset grid reloads (new
  // uploads may fix rows; deletes may remove them).
  useEffect(() => {
    api.get("/cloudflare-stream/missing-source")
      .then((r) => setMissingSource(r.data?.assets || []))
      .catch(() => { /* noop */ });
    loadTrashSummary();
  }, [assets.length]);

  const saveAssetCategories = async (assetId, categoryIds, meta, channelIds) => {
    try {
      await api.post(`/assets/${assetId}/categories`, { category_ids: categoryIds, channel_ids: channelIds || [] });
      let patched = {};
      if (meta) {
        const r = await api.patch(`/assets/${assetId}`, meta);
        patched = { artist_name: meta.artist_name, release_year: meta.release_year };
        void r;
      }
      setAssets((p) => p.map((a) => a.id === assetId ? { ...a, category_ids: categoryIds, channel_ids: channelIds || [], ...patched } : a));
      toast.success("Metadata updated");
    } catch { toast.error("Save failed"); }
  };

  // Dedicated edit modal save — song title / artist / release year ONLY.
  // Does not touch categories, trim, schedule or destinations.
  const saveAssetMeta = async (assetId, meta) => {
    try {
      const r = await api.patch(`/assets/${assetId}`, meta);
      setAssets((p) => p.map((a) => a.id === assetId ? { ...a, ...r.data } : a));
      setPreviewAsset((p) => (p && p.id === assetId ? { ...p, ...r.data } : p));
      toast.success("Asset details saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const regenAllMetadata = async () => {
    try {
      const r = await api.post("/ai/metadata-bulk/regen-all", null, { params: { only_missing: true } });
      if (!r.data.queued) { toast.info("Every asset already has artist & year"); return; }
      toast.success(`Extracting metadata for ${r.data.queued} asset(s)…`);
      setRegen({ running: true, total: r.data.queued, done: 0, filled: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start regen");
    }
  };

  // Poll bulk-regen progress while a job is running.
  useEffect(() => {
    if (!regen?.running) return;
    const iv = setInterval(async () => {
      try {
        const r = await api.get("/ai/metadata-bulk/regen-status");
        setRegen(r.data);
        if (!r.data.running) {
          clearInterval(iv);
          toast.success(`Metadata done · filled ${r.data.filled} of ${r.data.total}${r.data.errors ? ` · ${r.data.errors} error(s)` : ""}`);
          load();
        }
      } catch { /* noop */ }
    }, 2000);
    return () => clearInterval(iv);
  }, [regen?.running]);

  // Poll for CDN status updates while any asset is queued/publishing
  useEffect(() => {
    const pending = assets.some((a) => a.cdn_status === "queued" || a.cdn_status === "publishing");
    if (!pending) return;
    const t = setInterval(() => {
      const params = {};
      if (type !== "all") params.type = type;
      if (q) params.q = q;
      api.get("/assets", { params }).then((r) => setAssets(r.data)).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [assets, type, q]);

  const republishCdn = async (id) => {
    setCdnBusyId(id);
    setAssets((p) => p.map((a) => a.id === id ? { ...a, cdn_status: "publishing", cdn_error: null } : a));
    try {
      const r = await api.post(`/cloudflare-stream/publish/${id}`);
      setAssets((p) => p.map((a) => a.id === id ? {
        ...a,
        cdn_status: "ready",
        cloudflare_stream_uid: r.data.cloudflare_stream_uid,
        cloudflare_hls_url: r.data.hls_url,
        cloudflare_dash_url: r.data.dash_url,
        cloudflare_thumb_url: r.data.thumb_url,
        cdn_error: null,
      } : a));
      toast.success("Published to Cloudflare Stream");
    } catch (e) {
      const msg = e?.response?.data?.detail || "CDN publish failed";
      setAssets((p) => p.map((a) => a.id === id ? { ...a, cdn_status: "failed", cdn_error: msg } : a));
      toast.error(msg);
    }
    setCdnBusyId(null);
  };

  const genMeta = async (id) => {
    setBusyId(id);
    try {
      const r = await api.post(`/ai/metadata/${id}`);
      setAssets((p) => p.map((a) => a.id === id ? { ...a, ai_metadata: r.data.ai_metadata } : a));
      toast.success("AI metadata generated");
    } catch {
      toast.error("Failed to generate metadata");
    }
    setBusyId(null);
  };

  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkCatOpen, setBulkCatOpen] = useState(false);
  const [bulkSchedOpen, setBulkSchedOpen] = useState(false);
  const [replaceTargetId, setReplaceTargetId] = useState(null);
  const [batchRestoreOpen, setBatchRestoreOpen] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const replaceInputRef = useRef(null);
  const triggerReplace = (id) => { setReplaceTargetId(id); replaceInputRef.current?.click(); };
  const onReplaceFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    const id = replaceTargetId;
    if (!f || !id) return;
    const form = new FormData();
    form.append("file", f);
    toast.info("Restoring source…");
    try {
      const r = await api.post(`/assets/${id}/replace-source`, form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
      setAssets((p) => p.map((a) => a.id === id ? { ...a, ...r.data } : a));
      toast.success("Source restored — HLS re-generating");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    }
  };
  const toggleSelect = (id) => setSelectedIds((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);
  const allSelected = assets.length > 0 && selectedIds.length === assets.length;
  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : assets.map((a) => a.id));
  const selectByType = (t) => setSelectedIds(assets.filter((a) => a.type === t).map((a) => a.id));
  const selectUntagged = () => setSelectedIds(assets.filter((a) => !(a.category_ids && a.category_ids.length)).map((a) => a.id));
  const selectByTag = (tag) => { if (tag) setSelectedIds(assets.filter((a) => (a.tags || []).includes(tag)).map((a) => a.id)); };
  const bulkAddToSchedule = async ({ channel_id, day_of_week, start_time }) => {
    if (!selectedIds.length) return;
    const ids = [...selectedIds];
    try {
      const r = await api.post("/schedule/bulk-add-assets", { channel_id, day_of_week, start_time, asset_ids: ids });
      setBulkSchedOpen(false);
      toast.success(`Added ${r.data.added} item(s) to the grid${r.data.skipped ? `, ${r.data.skipped} skipped (past midnight)` : ""}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Add to schedule failed");
    }
  };
  const confirmBulkDelete = async (reason) => {
    if (!selectedIds.length) return;
    setBulkDeleting(true);
    const ids = [...selectedIds];
    try {
      const r = await api.post("/assets/bulk-delete", { asset_ids: ids, reason: reason || null });
      setAssets((p) => p.filter((a) => !ids.includes(a.id)));
      setSelectedIds([]);
      setBulkDeleteOpen(false);
      loadTrashSummary();
      toast.success(`Moved ${r.data.deleted} asset${r.data.deleted === 1 ? "" : "s"} to Trash`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulkDeleting(false);
    }
  };
  const bulkCategorize = async (categoryIds, mode) => {
    if (!selectedIds.length || !categoryIds.length) return;
    const ids = [...selectedIds];
    try {
      const r = await api.post("/assets/bulk-categorize", { asset_ids: ids, category_ids: categoryIds, mode });
      setAssets((p) => p.map((a) => {
        if (!ids.includes(a.id)) return a;
        const old = a.category_ids || [];
        const next = mode === "replace" ? [...categoryIds] : Array.from(new Set([...old, ...categoryIds]));
        return { ...a, category_ids: next };
      }));
      setBulkCatOpen(false);
      toast.success(`Updated categories on ${r.data.updated} asset${r.data.updated === 1 ? "" : "s"}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk categorize failed");
    }
  };

  const removeAsset = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? It will be moved to Trash (recoverable for 30 days).`)) return;
    try {
      await api.delete(`/assets/${id}`);
      setAssets((p) => p.filter((a) => a.id !== id));
      loadTrashSummary();
      toast.success("Moved to Trash");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div className="p-4">
      <div className="flex items-baseline justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-500 dark:text-cyan-300">§ Asset Library</div>
          <h1 className="font-display font-black text-2xl mt-1">Media assets</h1>
          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2 flex-wrap">
            <span>{assets.length} asset{assets.length === 1 ? "" : "s"} · drag any into the <Link to="/console/scheduler" className="underline hover:text-foreground">Scheduler</Link> to add to a playlist</span>
            {r2Enabled ? (
              <Badge
                data-testid="cdn-global-pill"
                className="bg-cyan-500/15 text-cyan-600 dark:text-cyan-300 border border-cyan-400/40 text-[9px] uppercase font-mono flex items-center gap-1"
              >
                <Cloud className="w-2.5 h-2.5" /> Cloudflare R2 active
              </Badge>
            ) : cdnEnabled ? (
              <Badge
                data-testid="cdn-global-pill"
                className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-400/40 text-[9px] uppercase font-mono flex items-center gap-1"
              >
                <Cloud className="w-2.5 h-2.5" /> Cloudflare Stream CDN active
              </Badge>
            ) : (
              <Badge
                data-testid="cdn-global-pill"
                className="bg-muted/40 text-muted-foreground border border-border text-[9px] uppercase font-mono flex items-center gap-1"
              >
                <CloudOff className="w-2.5 h-2.5" /> CDN not configured
              </Badge>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-2 top-2.5 text-muted-foreground" />
            <Input
              placeholder="Search by song, artist, year, upload date"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="pl-8 w-72 bg-background border-border rounded-sm"
              data-testid="asset-search"
            />
          </div>
          {cdnEnabled && assets.some((a) => a.cdn_status === "failed") && (
            <Button
              onClick={async () => {
                try {
                  const r = await api.post("/cloudflare-stream/retry-failed");
                  toast.success(`Re-queued ${r.data.queued} failed asset${r.data.queued === 1 ? "" : "s"}`);
                  setAssets((p) => p.map((a) => a.cdn_status === "failed" && a.storage_path
                    ? { ...a, cdn_status: "queued", cdn_error: null } : a));
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Bulk retry failed");
                }
              }}
              data-testid="assets-retry-all-failed-btn"
              variant="outline"
              className="rounded-sm h-10 px-3 border-fuchsia-500/40 text-fuchsia-500 hover:bg-fuchsia-500/10 font-display font-bold text-sm"
            >
              <AlertTriangle className="w-4 h-4 mr-2" /> Retry all failed
            </Button>
          )}
          <Button
            onClick={regenAllMetadata}
            disabled={regen?.running}
            data-testid="assets-regen-all-btn"
            variant="outline"
            className="rounded-sm h-10 px-3 border-fuchsia-500/40 text-fuchsia-500 hover:bg-fuchsia-500/10 font-display font-bold text-sm"
          >
            {regen?.running
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Regen {regen.done}/{regen.total}</>
              : <><Sparkles className="w-4 h-4 mr-2" /> Regen all metadata</>}
          </Button>
          {canWrite && (
            <Button
              onClick={() => setTrashOpen(true)}
              data-testid="assets-trash-btn"
              variant="outline"
              className="rounded-sm h-10 px-3 border-border text-muted-foreground hover:text-foreground hover:bg-muted font-display font-bold text-sm relative"
            >
              <History className="w-4 h-4 mr-2" /> Trash
              {trashSummary.count > 0 && (
                <span data-testid="trash-count-badge" className={`ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold ${trashSummary.expiring_soon > 0 ? "bg-amber-500 text-black" : "bg-muted-foreground/20 text-foreground"}`}>
                  {trashSummary.count}
                </span>
              )}
            </Button>
          )}
          {canWrite && (
            <Button
              onClick={() => setBatchRestoreOpen(true)}
              data-testid="assets-batch-restore-btn"
              variant="outline"
              className="rounded-sm h-10 px-3 border-cyan-500/40 text-cyan-500 hover:bg-cyan-500/10 font-display font-bold text-sm"
            >
              <Upload className="w-4 h-4 mr-2" /> Restore sources
            </Button>
          )}
          <Button
            onClick={() => setUploadOpen(true)}
            data-testid="asset-upload-open-btn"
            className="tenant-btn rounded-sm h-10 px-4 font-display font-bold text-sm"
          >
            <Upload className="w-4 h-4 mr-2" /> Upload media
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1 mb-4">
        {TYPES.map((t) => (
          <button
            key={t}
            onClick={() => setType(t)}
            data-testid={`asset-filter-${t}`}
            className={`text-[10px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-sm border ${
              type === t
                ? "border-foreground bg-foreground text-background"
                : "border-border text-muted-foreground hover:border-muted-foreground hover:text-foreground"
            }`}
          >
            {t}
          </button>
        ))}
        <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
          <button
            onClick={() => setFullLengthOnly((v) => !v)}
            data-testid="asset-fulllength-filter"
            title="Show only songs with no OUT point (they play to the very end)"
            className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1.5 rounded-sm border transition-colors ${fullLengthOnly ? "border-amber-500 bg-amber-500/10 text-amber-300" : "border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground"}`}
          >
            Songs playing full length
          </button>
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Sort</span>
          <select
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value)}
            data-testid="asset-sort"
            className="bg-background border border-border rounded-sm text-xs px-2 py-1.5"
          >
            <option value="default">Default</option>
            <option value="name">Song name (A–Z)</option>
            <option value="artist">Artist (A–Z)</option>
            <option value="year">Release year (newest)</option>
            <option value="upload">Upload date (newest)</option>
          </select>
          <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Category</span>
          <select
            value={catFilter}
            onChange={(e) => setCatFilter(e.target.value)}
            data-testid="asset-category-filter"
            className="bg-background border border-border rounded-sm text-xs px-2 py-1.5 max-w-[220px]"
          >
            <option value="all">All categories</option>
            {allCategories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}{typeof c.track_count === "number" ? ` (${c.track_count})` : ""}
              </option>
            ))}
          </select>
          {catFilter !== "all" && (
            <button
              onClick={() => setCatFilter("all")}
              data-testid="asset-category-filter-clear"
              className="text-[10px] font-mono uppercase tracking-widest px-2 py-1.5 rounded-sm border border-border text-muted-foreground hover:text-foreground hover:border-muted-foreground"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Cloudflare R2 storage status + migration (collapsible, collapsed by default) */}
      <div className="panel px-3 py-2">
        <button
          onClick={() => setR2Open((v) => !v)}
          data-testid="r2-panel-toggle"
          className="w-full flex items-center justify-between gap-2 text-left"
        >
          <span className="inline-flex items-center gap-2 text-xs font-mono">
            <Cloud className="w-3.5 h-3.5 text-cyan-500" />
            <span className="uppercase tracking-widest text-muted-foreground">Cloudflare R2 storage</span>
            <span className="text-emerald-500">· {assets.length} assets</span>
          </span>
          <span className={`text-muted-foreground transition-transform ${r2Open ? "rotate-90" : ""}`}>▸</span>
        </button>
        {r2Open && <div className="mt-2"><R2StoragePanel /></div>}
      </div>

      {/* Trash auto-alert — warn when deleted assets are about to expire */}
      {canWrite && trashSummary.expiring_soon > 0 && !trashAlertDismissed && (
        <div
          data-testid="trash-expiry-alert"
          className="mb-4 rounded-sm border border-amber-500/50 bg-amber-500/10 backdrop-blur-md px-4 py-3 flex items-center justify-between gap-3"
        >
          <div className="flex items-center gap-2 text-sm">
            <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
            <span>
              <span className="font-bold">{trashSummary.expiring_soon}</span> deleted asset{trashSummary.expiring_soon === 1 ? "" : "s"} will be permanently purged within {trashSummary.warn_days} days
              {trashSummary.soonest_expiry_days != null && <> (soonest in ~{trashSummary.soonest_expiry_days}d)</>}. Restore anything you still need.
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button size="sm" onClick={() => setTrashOpen(true)} data-testid="trash-alert-open-btn" className="rounded-sm tenant-btn text-xs h-8">
              Open Trash
            </Button>
            <button onClick={() => setTrashAlertDismissed(true)} data-testid="trash-alert-dismiss" className="text-muted-foreground hover:text-foreground p-1" title="Dismiss">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Re-Upload Watchlist — assets whose source binary is gone. Nothing
          else silently rots: if it lives in the DB but not in storage,
          it lists here. Collapsible so it doesn't dominate the page. */}
      {missingSource.length > 0 && (
        <div
          data-testid="missing-source-panel"
          className="mb-4 rounded-sm border border-amber-500/40 bg-amber-500/5 backdrop-blur-md"
        >
          <button
            type="button"
            onClick={() => setMissingCollapsed((v) => !v)}
            data-testid="missing-source-toggle"
            className="w-full flex items-center justify-between px-4 py-2 hover:bg-amber-500/10 transition-colors"
          >
            <div className="flex items-center gap-2 text-xs">
              <AlertTriangle className="w-4 h-4 text-amber-500" />
              <span className="font-mono uppercase tracking-widest text-[10px] text-amber-600 dark:text-amber-400">
                Missing source · {missingSource.length}
              </span>
              <span className="text-muted-foreground hidden sm:inline">
                these assets have a DB row but no stored file — re-upload to bring them back on-air
              </span>
            </div>
            <span className="text-[10px] font-mono text-muted-foreground">
              {missingCollapsed ? "SHOW" : "HIDE"}
            </span>
          </button>
          {!missingCollapsed && (
            <div className="border-t border-amber-500/20 max-h-64 overflow-y-auto scrollbar-thin">
              {missingSource.map((a) => (
                <div
                  key={a.id}
                  data-testid={`missing-source-row-${a.id}`}
                  className="px-4 py-2 flex items-center gap-3 text-xs border-b border-amber-500/10 last:border-b-0 hover:bg-amber-500/10"
                >
                  <CloudOff className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-mono" title={a.name}>{a.name || a.filename || a.id}</div>
                    <div className="text-[10px] text-muted-foreground truncate">
                      {a.duration_sec ? `${Math.round(a.duration_sec)}s · ` : ""}
                      status: <span className="uppercase">{a.cdn_status || "unknown"}</span>
                      {a.cdn_error ? ` · ${a.cdn_error}` : ""}
                    </div>
                  </div>
                  <Button
                    onClick={() => setUploadOpen(true)}
                    data-testid={`missing-source-reupload-${a.id}`}
                    size="sm"
                    variant="outline"
                    className="rounded-sm h-7 text-[10px] font-mono uppercase tracking-widest border-amber-500/40 text-amber-600 dark:text-amber-300"
                  >
                    <Upload className="w-3 h-3 mr-1" /> Re-upload
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {assets.length === 0 ? (
        <div className="panel rounded-2xl p-12 text-center border border-dashed border-border">
          <FileVideo className="w-10 h-10 text-muted-foreground mx-auto mb-3" strokeWidth={1.25} />
          <div className="font-display font-bold text-lg">No {type === "all" ? "" : type + " "}assets yet</div>
          <div className="text-sm text-muted-foreground mt-1">Upload your first video to start building playlists.</div>
          <Button
            onClick={() => setUploadOpen(true)}
            data-testid="asset-empty-upload-btn"
            className="mt-5 tenant-btn rounded-full h-10 px-6 font-display font-bold text-sm"
          >
            <Upload className="w-4 h-4 mr-2" /> Upload first video
          </Button>
        </div>
      ) : (
        <div className="grid gap-2">
          <div className="flex items-center justify-between gap-3 flex-wrap px-1" data-testid="asset-bulk-toolbar">
            <div className="flex items-center gap-3 flex-wrap">
              <label className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  data-testid="asset-select-all"
                  className="w-4 h-4 accent-fuchsia-500 cursor-pointer"
                />
                {selectedIds.length ? `${selectedIds.length} selected` : "Select all"}
              </label>
              <button
                onClick={() => setGroupDupes((v) => !v)}
                data-testid="asset-group-dupes-toggle"
                title={dupGroupCount ? `${dupGroupCount} set(s) of duplicates found` : "No duplicates detected"}
                className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded-sm border transition-colors ${
                  groupDupes
                    ? "border-amber-500/50 text-amber-600 dark:text-amber-300 bg-amber-500/10"
                    : "border-border text-muted-foreground hover:bg-muted"
                }`}
              >
                {groupDupes ? <CopyCheck className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                Group duplicates
                {dupGroupCount > 0 && (
                  <span className="ml-0.5 px-1 rounded-sm bg-amber-500/20 text-amber-600 dark:text-amber-300" data-testid="asset-dupe-group-count">
                    {dupGroupCount}
                  </span>
                )}
              </button>
              <div className="flex items-center gap-1.5 flex-wrap" data-testid="asset-quick-select">
                <span className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">Quick:</span>
                {["program", "promo", "ad", "bumper", "slate"].filter((t) => assets.some((a) => a.type === t)).map((t) => (
                  <button
                    key={t}
                    onClick={() => selectByType(t)}
                    data-testid={`asset-quick-${t}`}
                    className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border border-border hover:bg-muted transition-colors"
                  >
                    {t}s
                  </button>
                ))}
                {assets.some((a) => !(a.category_ids && a.category_ids.length)) && (
                  <button
                    onClick={selectUntagged}
                    data-testid="asset-quick-untagged"
                    className="text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-sm border border-amber-500/40 text-amber-600 dark:text-amber-300 hover:bg-amber-500/10 transition-colors"
                  >
                    Untagged
                  </button>
                )}
                {(() => {
                  const uniqueTags = Array.from(new Set(assets.flatMap((a) => a.tags || []))).sort().slice(0, 60);
                  return uniqueTags.length > 0 ? (
                    <select
                      onChange={(e) => { selectByTag(e.target.value); e.target.value = ""; }}
                      defaultValue=""
                      data-testid="asset-quick-tag-select"
                      className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-border bg-background hover:bg-muted transition-colors cursor-pointer"
                    >
                      <option value="" disabled>By tag…</option>
                      {uniqueTags.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  ) : null;
                })()}
              </div>
            </div>
            {selectedIds.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => setSelectedIds([])}
                  data-testid="asset-clear-selection"
                  className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground"
                >
                  Clear
                </button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setBulkSchedOpen(true)}
                  data-testid="asset-bulk-schedule-btn"
                  className="rounded-sm h-8 text-xs border-cyan-500/40 text-cyan-600 dark:text-cyan-300 hover:bg-cyan-500/10"
                >
                  <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Add to grid ({selectedIds.length})
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setBulkCatOpen(true)}
                  data-testid="asset-bulk-categorize-btn"
                  className="rounded-sm h-8 text-xs border-fuchsia-500/40 text-fuchsia-600 dark:text-fuchsia-300 hover:bg-fuchsia-500/10"
                >
                  <Tags className="w-3.5 h-3.5 mr-1" /> Categorize ({selectedIds.length})
                </Button>
                <Button
                  size="sm"
                  onClick={() => setBulkDeleteOpen(true)}
                  data-testid="asset-bulk-delete-btn"
                  className="rounded-sm h-8 text-xs bg-rose-600 hover:bg-rose-500 text-white"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete ({selectedIds.length})
                </Button>
              </div>
            )}
          </div>
          {orderedAssets.map((a) => {
            const _k = dupKey(a);
            const _n = dupCounts[_k] || 1;
            const _ki = dupIndex[a.id];
            const _isDup = _n > 1;
            const _isHead = groupDupes && _isDup && _ki === 1;
            const _fmtDur = (s) => {
              s = Math.round(s || 0); const m = Math.floor(s / 60); const r = s % 60;
              return m ? `${m}m ${r}s` : `${r}s`;
            };
            return (
            <React.Fragment key={a.id}>
            {_isHead && (
              <div className="flex items-center gap-2 mt-2 first:mt-0 px-1" data-testid={`asset-dupe-group-${_k}`}>
                <span className="h-px flex-1 bg-amber-400/25" />
                <span className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-amber-600 dark:text-amber-300">
                  <CopyCheck className="w-3.5 h-3.5" />
                  {a.name} · {_fmtDur(a.effective_duration_sec || a.duration_sec)} — {_n} copies
                </span>
                <span className="h-px flex-1 bg-amber-400/25" />
              </div>
            )}
            <div className={`panel p-2 flex items-center gap-3 ${selectedIds.includes(a.id) ? "ring-1 ring-fuchsia-500/60" : ""} ${_isDup && groupDupes ? "border-l-2 border-l-amber-400/70" : ""}`} data-testid={`asset-row-${a.id}`}>
              {_isDup && groupDupes && (
                <span className="shrink-0 text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-sm bg-amber-500/15 text-amber-600 dark:text-amber-300 border border-amber-400/30" data-testid={`asset-dupe-badge-${a.id}`} title={`Copy ${_ki} of ${_n} — same name & duration`}>
                  {_ki}/{_n}
                </span>
              )}
              <input
                type="checkbox"
                checked={selectedIds.includes(a.id)}
                onChange={() => toggleSelect(a.id)}
                data-testid={`asset-select-${a.id}`}
                aria-label={`Select ${a.name}`}
                className="w-4 h-4 accent-fuchsia-500 cursor-pointer shrink-0"
              />
              <button
                type="button"
                onClick={() => a.source_url && setPreviewAsset(a)}
                disabled={!a.source_url}
                data-testid={`asset-preview-btn-${a.id}`}
                title={a.source_url ? "Preview" : "No file to preview"}
                className={`group w-24 aspect-video bg-black flex items-center justify-center relative border border-border overflow-hidden rounded-sm shrink-0 ${a.source_url ? "cursor-pointer hover:border-fuchsia-400/60" : "cursor-not-allowed opacity-70"}`}
              >
                {a.thumbnail_url ? (
                  <img
                    src={withAuth(a.thumbnail_url)}
                    alt={a.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                ) : (
                  <Play className="w-6 h-6 text-muted-foreground" />
                )}
                {a.source_url && (
                  <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--brand-gradient)" }}>
                      <Play className="w-4 h-4 text-white ml-0.5" fill="white" />
                    </div>
                  </div>
                )}
                <span className="absolute bottom-1 right-1 text-[9px] font-mono bg-black/70 text-white px-1 rounded-sm">
                  {fmtDur(a.duration_sec)}
                </span>
              </button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-display font-bold text-sm truncate">{a.name}</div>
                  <Badge variant="outline" className="text-[9px] uppercase font-mono border-border">
                    {a.type}
                  </Badge>
                  <Badge variant="outline" className="text-[9px] uppercase font-mono border-border">
                    {a.resolution}
                  </Badge>
                  {a.codec && a.codec !== "unknown" && (
                    <Badge variant="outline" className="text-[9px] uppercase font-mono border-border">
                      {a.codec}
                    </Badge>
                  )}
                  {a.breakpoints?.length > 0 && (
                    <Badge className="bg-amber-500/15 text-amber-600 dark:text-amber-300 border border-amber-400/40 text-[9px] uppercase font-mono">
                      {a.breakpoints.length} breaks
                    </Badge>
                  )}
                  {a.ingest_status && a.ingest_status !== "ready" && (
                    <Badge className="text-[9px] uppercase font-mono">
                      {a.ingest_status}
                    </Badge>
                  )}
                  {cdnEnabled && <CdnPill asset={a} onRetry={republishCdn} busy={cdnBusyId === a.id} />}
                  {a.faststart === true && (
                    <Badge
                      className="bg-violet-500/15 text-violet-600 dark:text-violet-300 border border-violet-400/40 text-[9px] uppercase font-mono flex items-center gap-1"
                      data-testid={`asset-faststart-badge-${a.id}`}
                      title="MP4 faststart: moov index is at the front for instant live start"
                    >
                      <Zap className="w-2.5 h-2.5" strokeWidth={2.5} /> faststart ✓
                    </Badge>
                  )}
                  {a.faststart === false && a.faststart_checked && (
                    <Badge
                      className="bg-muted/40 text-muted-foreground border border-border text-[9px] uppercase font-mono flex items-center gap-1"
                      data-testid={`asset-faststart-pending-${a.id}`}
                      title={a.faststart_error || "MP4 not faststart-optimized yet"}
                    >
                      not optimized
                    </Badge>
                  )}
                  {(a.trim_in_sec != null || a.trim_out_sec != null) && (
                    <Badge
                      className="bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-300 border border-fuchsia-400/40 text-[9px] uppercase font-mono flex items-center gap-1"
                      data-testid={`asset-trim-badge-${a.id}`}
                      title={`Trimmed · effective ${fmtDur(a.effective_duration_sec ?? a.duration_sec)}`}
                    >
                      <Scissors className="w-2.5 h-2.5" strokeWidth={2} />
                      {fmtClock(a.trim_in_sec ?? 0)}–{fmtClock(a.trim_out_sec ?? a.duration_sec)}
                      <span className="opacity-70">· {fmtDur(a.effective_duration_sec ?? a.duration_sec)}</span>
                    </Badge>
                  )}
                  {a.cut_in_sec != null && a.source_duration_sec != null && (
                    <Badge
                      className="bg-fuchsia-500/15 text-fuchsia-600 dark:text-fuchsia-300 border border-fuchsia-400/40 text-[9px] uppercase font-mono flex items-center gap-1"
                      data-testid={`asset-cut-badge-${a.id}`}
                      title={`R2 physically cut to ${fmtClock(a.cut_in_sec)}–${fmtClock(a.cut_out_sec ?? a.source_duration_sec)} · original ${fmtDur(a.source_duration_sec)} preserved`}
                    >
                      <Scissors className="w-2.5 h-2.5" strokeWidth={2} />
                      cut · {fmtDur(a.duration_sec)}
                      <span className="opacity-70">of {fmtDur(a.source_duration_sec)}</span>
                    </Badge>
                  )}
                </div>
                {(a.artist_name || a.release_year) && (
                  <div className="text-xs text-foreground/80 mt-0.5 truncate" data-testid={`asset-meta-${a.id}`}>
                    {a.artist_name || "Unknown artist"}{a.release_year ? ` · ${a.release_year}` : ""}
                  </div>
                )}
                {a.ai_metadata?.synopsis && (
                  <div className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{a.ai_metadata.synopsis}</div>
                )}
              </div>
              <div className="shrink-0 flex items-center gap-1.5">
                {cdnEnabled && (a.cdn_status === "failed" || a.cdn_status === "none" || !a.cdn_status) && a.source_url && (
                  <Button
                    variant="outline" size="sm"
                    onClick={() => republishCdn(a.id)}
                    disabled={cdnBusyId === a.id}
                    data-testid={`asset-cdn-retry-${a.id}`}
                    className="rounded-sm border-amber-500/40 text-amber-600 dark:text-amber-300 hover:bg-amber-500/10 text-xs h-8"
                    title={a.cdn_error || "Publish to CDN"}
                  >
                    {cdnBusyId === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CloudUpload className="w-3.5 h-3.5" />}
                  </Button>
                )}
                {busyId === a.id && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="icon" className="rounded-sm border-border h-8 w-8" data-testid={`asset-actions-${a.id}`} title="Actions" aria-label={`Actions for ${a.name}`}>
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-52">
                    {a.source_url && (
                      <DropdownMenuItem onClick={() => setPreviewAsset(a)} data-testid={`asset-trim-btn-${a.id}`}>
                        <Scissors className="w-3.5 h-3.5 mr-2" /> Trim{(a.trim_in_sec != null || a.trim_out_sec != null) ? " ✓" : ""}
                      </DropdownMenuItem>
                    )}
                    {a.source_url && (
                      <DropdownMenuItem onClick={() => setClipAsset(a)} data-testid={`asset-clips-btn-${a.id}`}>
                        <Clapperboard className="w-3.5 h-3.5 mr-2" /> Social clips
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={() => genMeta(a.id)} disabled={busyId === a.id} data-testid={`asset-ai-meta-${a.id}`}>
                      <Sparkles className="w-3.5 h-3.5 mr-2" /> AI metadata
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setEditAsset(a)} data-testid={`asset-edit-${a.id}`}>
                      <Pencil className="w-3.5 h-3.5 mr-2" /> Edit details
                    </DropdownMenuItem>
                    {allCategories.length > 0 && (
                      <DropdownMenuItem onClick={() => setAssignAsset(a)} data-testid={`asset-categories-${a.id}`}>
                        <Music className="w-3.5 h-3.5 mr-2" /> Categories {a.category_ids?.length ? `(${a.category_ids.length})` : ""}
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem asChild data-testid={`asset-open-${a.id}`}>
                      <Link to={`/console/assets/${a.id}/breakpoints`}><Film className="w-3.5 h-3.5 mr-2" /> Breakpoints</Link>
                    </DropdownMenuItem>
                    {canWrite && (
                      <DropdownMenuItem onClick={() => triggerReplace(a.id)} data-testid={`asset-replace-source-${a.id}`}>
                        <Upload className="w-3.5 h-3.5 mr-2" /> Restore source
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => removeAsset(a.id, a.name)} data-testid={`asset-delete-${a.id}`} className="text-rose-500 focus:text-rose-500">
                      <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
            </React.Fragment>
            );
          })}
        </div>
      )}

      <UploadDialog
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onUploaded={(newAsset) => setAssets((p) => [newAsset, ...p])}
      />
      <input
        ref={replaceInputRef}
        type="file"
        accept="video/*"
        className="hidden"
        onChange={onReplaceFile}
        data-testid="replace-source-input"
      />
      <BatchRestoreDialog
        open={batchRestoreOpen}
        assets={assets}
        onClose={() => setBatchRestoreOpen(false)}
        onReplaced={(id, data) => setAssets((p) => p.map((a) => a.id === id ? { ...a, ...data } : a))}
      />
      <TrashDialog
        open={trashOpen}
        onClose={() => setTrashOpen(false)}
        onChanged={() => { load(); loadTrashSummary(); }}
        onRestored={(asset) => setAssets((p) => {
          const exists = p.some((a) => a.id === asset.id);
          return exists ? p.map((a) => a.id === asset.id ? asset : a) : [asset, ...p];
        })}
      />
      <AssetPreviewPlayer
        asset={previewAsset}
        open={!!previewAsset}
        onClose={() => setPreviewAsset(null)}
        onUpdated={(updated) => {
          setAssets((p) => p.map((a) => (a.id === updated.id ? { ...a, ...updated } : a)));
          setPreviewAsset((p) => (p && p.id === updated.id ? { ...p, ...updated } : p));
        }}
      />
      <SocialClipsDialog
        asset={clipAsset}
        open={!!clipAsset}
        onClose={() => setClipAsset(null)}
      />
      <AssignCategoriesDialog
        asset={assignAsset}
        categories={allCategories}
        channels={channelsList}
        onClose={() => setAssignAsset(null)}
        onSave={(ids, meta, chIds) => { saveAssetCategories(assignAsset.id, ids, meta, chIds); setAssignAsset(null); }}
      />
      <EditAssetDialog
        asset={editAsset}
        onClose={() => setEditAsset(null)}
        onSave={(meta) => { saveAssetMeta(editAsset.id, meta); setEditAsset(null); }}
      />
      <BulkDeleteConfirmDialog
        open={bulkDeleteOpen}
        count={selectedIds.length}
        busy={bulkDeleting}
        onConfirm={confirmBulkDelete}
        onClose={() => setBulkDeleteOpen(false)}
      />
      <BulkCategorizeDialog
        open={bulkCatOpen}
        count={selectedIds.length}
        categories={allCategories}
        onSave={bulkCategorize}
        onClose={() => setBulkCatOpen(false)}
      />
      <BulkScheduleDialog
        open={bulkSchedOpen}
        count={selectedIds.length}
        channels={channelsList}
        onSave={bulkAddToSchedule}
        onClose={() => setBulkSchedOpen(false)}
      />
    </div>
  );
}

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

function normName(s) {
  return (s || "").toLowerCase().replace(/\.[^.]+$/, "").replace(/[^a-z0-9]+/g, "");
}

function BatchRestoreDialog({ open, assets, onClose, onReplaced }) {
  const [rows, setRows] = useState([]);  // {file, assetId, assetName, status}
  const [running, setRunning] = useState(false);
  useEffect(() => { if (!open) { setRows([]); setRunning(false); } }, [open]);

  const onPick = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    const mapped = files.map((file) => {
      const n = normName(file.name);
      const matches = assets.filter((a) => normName(a.name) === n);
      const match = matches.length === 1 ? matches[0] : null;
      return {
        file,
        assetId: match?.id || null,
        assetName: match?.name || (matches.length > 1 ? "(ambiguous — rename to match one title)" : "(no matching asset)"),
        status: match ? "ready" : "no-match",
      };
    });
    setRows(mapped);
  };

  const run = async () => {
    setRunning(true);
    let done = 0;
    let failed = 0;
    for (let i = 0; i < rows.length; i++) {
      const r = rows[i];
      if (!r.assetId) continue;
      setRows((p) => p.map((x, idx) => idx === i ? { ...x, status: "uploading" } : x));
      try {
        const form = new FormData();
        form.append("file", r.file);
        const resp = await api.post(`/assets/${r.assetId}/replace-source`, form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 600000 });
        onReplaced(r.assetId, resp.data);
        done += 1;
        setRows((p) => p.map((x, idx) => idx === i ? { ...x, status: "done" } : x));
      } catch (err) {
        failed += 1;
        setRows((p) => p.map((x, idx) => idx === i ? { ...x, status: "failed" } : x));
      }
    }
    setRunning(false);
    if (done) toast.success(`Restored ${done} source${done === 1 ? "" : "s"}`);
    if (failed) toast.error(`${failed} source${failed === 1 ? "" : "s"} failed to restore`);
  };

  const readyCount = rows.filter((r) => r.assetId).length;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="batch-restore-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">Restore sources (batch)</DialogTitle>
          <DialogDescription>
            Select multiple original video files. Each is matched to an existing asset by name and re-uploaded into that same record (keeps IDs, schedule & categories; regenerates HLS).
          </DialogDescription>
        </DialogHeader>
        <div className="py-2">
          <input
            type="file"
            accept="video/*"
            multiple
            onChange={onPick}
            data-testid="batch-restore-input"
            className="block w-full text-xs file:mr-3 file:rounded-sm file:border file:border-border file:bg-muted file:px-3 file:py-1.5 file:text-xs"
          />
        </div>
        {rows.length > 0 && (
          <div className="max-h-56 overflow-auto space-y-1 py-1">
            {rows.map((r, i) => (
              <div key={i} data-testid={`batch-restore-row-${i}`} className="flex items-center justify-between gap-2 text-xs px-2 py-1 rounded-sm bg-muted/40">
                <span className="truncate flex-1" title={r.file.name}>{r.file.name}</span>
                <span className={`text-[10px] font-mono uppercase shrink-0 ${
                  r.status === "done" ? "text-emerald-500"
                    : r.status === "failed" ? "text-rose-500"
                    : r.status === "no-match" ? "text-amber-500"
                    : r.status === "uploading" ? "text-cyan-500"
                    : "text-muted-foreground"
                }`}>
                  {r.status === "ready" ? `→ ${r.assetName}` : r.status === "no-match" ? r.assetName : r.status}
                </span>
              </div>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={running}>Close</Button>
          <Button onClick={run} disabled={running || readyCount === 0} data-testid="batch-restore-run">
            {running ? "Restoring…" : `Restore ${readyCount} matched`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TrashDialog({ open, onClose, onRestored, onChanged }) {
  const [tab, setTab] = useState("trash");
  const [items, setItems] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(false);
  const [retention, setRetention] = useState(30);
  const [busyId, setBusyId] = useState(null);
  const [restoringAll, setRestoringAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/assets/trash");
      setItems(r.data.items || []);
      setRetention(r.data.retention_days || 30);
    } catch {
      toast.error("Failed to load trash");
    } finally {
      setLoading(false);
    }
  };
  const loadLog = async () => {
    setLoading(true);
    try {
      const r = await api.get("/assets/delete-log");
      setLog(r.data.items || []);
    } catch {
      toast.error("Failed to load delete log");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!open) { setItems([]); setLog([]); setBusyId(null); setTab("trash"); return; }
    if (tab === "trash") load(); else loadLog();
  }, [open, tab]);  // eslint-disable-line react-hooks/exhaustive-deps

  const restore = async (t) => {
    setBusyId(t.id);
    try {
      const r = await api.post(`/assets/trash/${t.id}/restore`);
      onRestored(r.data);
      setItems((p) => p.filter((x) => x.id !== t.id));
      onChanged?.();
      toast.success(`Restored “${t.name}”`);
    } catch {
      toast.error("Restore failed");
    } finally {
      setBusyId(null);
    }
  };
  const restoreAll = async () => {
    setRestoringAll(true);
    try {
      const r = await api.post("/assets/trash/restore-all");
      setItems([]);
      onChanged?.();
      toast.success(`Restored ${r.data.restored} asset${r.data.restored === 1 ? "" : "s"}`);
    } catch {
      toast.error("Restore all failed");
    } finally {
      setRestoringAll(false);
    }
  };
  const purge = async (t) => {
    if (!window.confirm(`Permanently delete “${t.name}”? This cannot be undone.`)) return;
    setBusyId(t.id);
    try {
      await api.delete(`/assets/trash/${t.id}`);
      setItems((p) => p.filter((x) => x.id !== t.id));
      onChanged?.();
      toast.success("Permanently deleted");
    } catch {
      toast.error("Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  const fmtWhen = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };
  const daysLeft = (iso) => {
    try {
      const t = new Date(iso).getTime();
      const left = retention - (Date.now() - t) / 86400000;
      return Math.max(0, Math.round(left));
    } catch { return null; }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="trash-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">Recently deleted</DialogTitle>
          <DialogDescription>
            Deleted assets are recoverable for {retention} days (their source file is moved to a trash area, not erased). Restore brings back the asset with its categories; permanently delete to free storage now.
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 border-b border-border pb-2">
          <button data-testid="trash-tab-trash" onClick={() => setTab("trash")} className={`text-xs font-mono uppercase tracking-widest px-2 py-1 rounded-sm ${tab === "trash" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"}`}>Trash{items.length ? ` (${items.length})` : ""}</button>
          <button data-testid="trash-tab-log" onClick={() => setTab("log")} className={`text-xs font-mono uppercase tracking-widest px-2 py-1 rounded-sm ${tab === "log" ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground"}`}>Delete log</button>
          {tab === "trash" && items.length > 0 && (
            <Button size="sm" onClick={restoreAll} disabled={restoringAll} data-testid="trash-restore-all-btn" className="ml-auto rounded-sm tenant-btn text-xs h-8">
              {restoringAll ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RotateCcw className="w-3 h-3 mr-1" /> Restore all ({items.length})</>}
            </Button>
          )}
        </div>
        <div className="py-2 max-h-[52vh] overflow-auto space-y-2">
          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : tab === "trash" ? (
            items.length === 0 ? (
              <div data-testid="trash-empty" className="text-sm text-muted-foreground py-8 text-center">
                Trash is empty. Deleted assets appear here for {retention} days.
              </div>
            ) : (
              items.map((t) => {
                const dl = daysLeft(t.trashed_at);
                const soon = dl != null && dl <= 7;
                return (
                  <div key={t.id} data-testid={`trash-row-${t.id}`} className="flex items-center justify-between gap-3 px-3 py-2 rounded-sm bg-muted/40 border border-border">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium" title={t.name}>{t.name || "(untitled)"}</div>
                      <div className="text-[11px] text-muted-foreground font-mono">
                        deleted {fmtWhen(t.trashed_at)}
                        {dl != null && <span className={soon ? "text-amber-500 font-bold" : ""}> · {dl}d left</span>}
                        {t.reason ? ` · “${t.reason}”` : ""}
                        {t.trash_r2_key ? "" : " · source not stored"}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button size="sm" onClick={() => restore(t)} disabled={busyId === t.id} data-testid={`trash-restore-${t.id}`} className="rounded-sm tenant-btn text-xs h-8">
                        {busyId === t.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RotateCcw className="w-3 h-3 mr-1" /> Restore</>}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => purge(t)} disabled={busyId === t.id} data-testid={`trash-purge-${t.id}`} className="rounded-sm text-xs h-8 border-rose-500/40 text-rose-500 hover:bg-rose-500/10">
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                );
              })
            )
          ) : (
            log.length === 0 ? (
              <div data-testid="delete-log-empty" className="text-sm text-muted-foreground py-8 text-center">No deletions recorded yet.</div>
            ) : (
              log.map((e, i) => (
                <div key={i} data-testid={`delete-log-row-${i}`} className="px-3 py-2 rounded-sm bg-muted/40 border border-border text-xs">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{e.extras?.count || 1} asset{(e.extras?.count || 1) === 1 ? "" : "s"} deleted</span>
                    <span className="text-muted-foreground font-mono">{fmtWhen(e.at)}</span>
                  </div>
                  <div className="text-muted-foreground mt-0.5">
                    by <span className="font-mono">{e.actor_email || e.actor_id || "unknown"}</span>
                    {e.extras?.reason ? ` · “${e.extras.reason}”` : ""}
                  </div>
                  {e.extras?.assets?.length > 0 && (
                    <div className="text-muted-foreground/80 mt-0.5 truncate" title={e.extras.assets.map((a) => a.name).join(", ")}>
                      {e.extras.assets.map((a) => a.name).join(", ")}
                    </div>
                  )}
                </div>
              ))
            )
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkScheduleDialog({ open, count, channels, onSave, onClose }) {
  const [channelId, setChannelId] = useState("");
  const [dow, setDow] = useState(0);
  const [time, setTime] = useState("06:00");
  useEffect(() => {
    if (open && channels.length && !channelId) setChannelId(channels[0].id);
  }, [open, channels, channelId]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="bulk-schedule-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Add {count} asset{count === 1 ? "" : "s"} to a grid</DialogTitle>
          <DialogDescription>
            Places the selected assets back-to-back on a channel's weekly grid, starting at the chosen weekday & time. Items running past midnight are skipped.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-2 py-2">
          <div className="col-span-3">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Channel</label>
            <select
              value={channelId}
              onChange={(e) => setChannelId(e.target.value)}
              data-testid="bulk-schedule-channel"
              className="mt-1 w-full rounded-sm bg-background border border-border h-8 text-sm px-2"
            >
              {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Weekday</label>
            <select
              value={dow}
              onChange={(e) => setDow(Number(e.target.value))}
              data-testid="bulk-schedule-weekday"
              className="mt-1 w-full rounded-sm bg-background border border-border h-8 text-sm px-2"
            >
              {WEEKDAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Start</label>
            <Input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              data-testid="bulk-schedule-time"
              className="mt-1 rounded-sm bg-background border-border h-8 text-sm font-mono"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => onSave({ channel_id: channelId, day_of_week: dow, start_time: time.length === 5 ? `${time}:00` : time })}
            disabled={!channelId}
            data-testid="bulk-schedule-save"
          >
            Add {count} to grid
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkDeleteConfirmDialog({ open, count, busy, onConfirm, onClose }) {
  const [text, setText] = useState("");
  const [reason, setReason] = useState("");
  useEffect(() => { if (!open) { setText(""); setReason(""); } }, [open]);
  const needsType = count > 10;
  const canDelete = !busy && count > 0 && (!needsType || text.trim().toUpperCase() === "DELETE");
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="bulk-delete-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2 text-rose-600 dark:text-rose-400">
            <AlertTriangle className="w-5 h-5" /> Delete {count} asset{count === 1 ? "" : "s"}?
          </DialogTitle>
          <DialogDescription>
            The selected {count} asset{count === 1 ? "" : "s"} will be moved to Trash and are recoverable for 30 days. Any schedule slots that used them fall back to the demo manifest until restored.
          </DialogDescription>
        </DialogHeader>
        <div className="py-1">
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Reason (optional — logged)</label>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            data-testid="bulk-delete-reason-input"
            placeholder="e.g. duplicate uploads, expired promos…"
            className="mt-1 rounded-sm bg-background border-border h-8 text-sm"
          />
        </div>
        {needsType && (
          <div className="py-1">
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">
              Type <span className="font-mono text-rose-500">DELETE</span> to confirm removing {count} assets
            </label>
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              data-testid="bulk-delete-confirm-input"
              placeholder="DELETE"
              className="mt-1 rounded-sm bg-background border-border h-8 text-sm font-mono"
            />
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={() => onConfirm(reason)} disabled={!canDelete} data-testid="bulk-delete-confirm-btn" className="bg-rose-600 hover:bg-rose-500 text-white disabled:opacity-50">
            {busy ? "Deleting…" : `Delete ${count}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkCategorizeDialog({ open, count, categories, onSave, onClose }) {
  const [selected, setSelected] = useState([]);
  const [mode, setMode] = useState("add");
  useEffect(() => { if (!open) { setSelected([]); setMode("add"); } }, [open]);
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="bulk-categorize-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Categorize {count} asset{count === 1 ? "" : "s"}</DialogTitle>
          <DialogDescription>Pick the music categories to apply to every selected asset.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 py-1">
          <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Mode</span>
          <button onClick={() => setMode("add")} data-testid="bulk-cat-mode-add" className={`text-[10px] font-mono uppercase px-2 py-1 rounded-sm border transition-colors ${mode === "add" ? "border-fuchsia-500 bg-fuchsia-500/10" : "border-border hover:bg-muted"}`}>Add to existing</button>
          <button onClick={() => setMode("replace")} data-testid="bulk-cat-mode-replace" className={`text-[10px] font-mono uppercase px-2 py-1 rounded-sm border transition-colors ${mode === "replace" ? "border-fuchsia-500 bg-fuchsia-500/10" : "border-border hover:bg-muted"}`}>Replace all</button>
        </div>
        <div className="max-h-60 overflow-auto grid grid-cols-2 gap-2 py-2">
          {categories.map((c) => (
            <label
              key={c.id}
              data-testid={`bulk-cat-${c.id}`}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs cursor-pointer border transition-colors ${selected.includes(c.id) ? "border-fuchsia-500 bg-fuchsia-500/10" : "border-border hover:bg-muted"}`}
            >
              <input type="checkbox" checked={selected.includes(c.id)} onChange={() => toggle(c.id)} className="sr-only" />
              <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: c.color }} />
              <span className="truncate">{c.name}</span>
            </label>
          ))}
          {categories.length === 0 && (
            <div className="col-span-2 text-xs text-muted-foreground">No categories yet — create some on the Categories page first.</div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(selected, mode)} disabled={!selected.length} data-testid="bulk-categorize-save">
            Apply to {count}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditAssetDialog({ asset, onClose, onSave }) {
  const [title, setTitle] = useState("");
  const [artist, setArtist] = useState("");
  const [year, setYear] = useState("");
  useEffect(() => {
    setTitle(asset?.name || "");
    setArtist(asset?.artist_name || "");
    setYear(asset?.release_year ? String(asset.release_year) : "");
  }, [asset]);
  if (!asset) return null;
  const submit = () => {
    const t = title.trim();
    if (!t) { toast.error("Song title is required"); return; }
    const yr = year.trim() ? parseInt(year.trim(), 10) : null;
    if (year.trim() && (Number.isNaN(yr) || yr < 1900 || yr > 2100)) {
      toast.error("Enter a valid release year (1900–2100)"); return;
    }
    onSave({ name: t, artist_name: artist.trim() || null, release_year: yr });
  };
  return (
    <Dialog open={!!asset} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="asset-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit asset details</DialogTitle>
          <DialogDescription>
            Song title, artist and release year. These power the on-air song-info lower-third; leave artist or year blank if unknown.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Song title</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Djadja" className="mt-1 rounded-sm bg-background border-border h-9 text-sm" data-testid="asset-edit-title-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Artist name</label>
              <Input value={artist} onChange={(e) => setArtist(e.target.value)} placeholder="e.g. Aya Nakamura" className="mt-1 rounded-sm bg-background border-border h-9 text-sm" data-testid="asset-edit-artist-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Release year</label>
              <Input value={year} onChange={(e) => setYear(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))} placeholder="e.g. 2023" className="mt-1 rounded-sm bg-background border-border h-9 text-sm font-mono" data-testid="asset-edit-year-input" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm border-border" data-testid="asset-edit-cancel">Cancel</Button>
          <Button onClick={submit} className="rounded-sm tenant-btn" data-testid="asset-edit-save">Save changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function AssignCategoriesDialog({ asset, categories, channels = [], onClose, onSave }) {
  const [selected, setSelected] = useState([]);
  const [selChannels, setSelChannels] = useState([]);
  const [artist, setArtist] = useState("");
  const [year, setYear] = useState("");
  useEffect(() => {
    setSelected(asset?.category_ids || []);
    setSelChannels(asset?.channel_ids || []);
    setArtist(asset?.artist_name || "");
    setYear(asset?.release_year ? String(asset.release_year) : "");
  }, [asset]);
  if (!asset) return null;
  const toggle = (id) => {
    setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };
  const toggleChannel = (id) => {
    setSelChannels((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  };
  const save = () => {
    const yr = year.trim() ? parseInt(year.trim(), 10) : null;
    onSave(selected, { artist_name: artist.trim() || null, release_year: Number.isNaN(yr) ? null : yr }, selChannels);
  };
  return (
    <Dialog open={!!asset} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="assign-categories-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Metadata for “{asset.name}”</DialogTitle>
          <DialogDescription>
            Pick every music category this track belongs to, and correct the on-air song-info (artist / year). Leave artist or year blank if unknown — the lower-third skips empty fields cleanly.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-2 py-1">
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Artist name</label>
            <Input value={artist} onChange={(e) => setArtist(e.target.value)} placeholder="e.g. Aya Nakamura" className="mt-1 rounded-sm bg-background border-border h-8 text-sm" data-testid="asset-artist-input" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Release year</label>
            <Input value={year} onChange={(e) => setYear(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))} placeholder="e.g. 2023" className="mt-1 rounded-sm bg-background border-border h-8 text-sm font-mono" data-testid="asset-year-input" />
          </div>
        </div>
        <div className="max-h-60 overflow-auto grid grid-cols-2 gap-2 py-2">
          {categories.map((c) => (
            <label
              key={c.id}
              className={`flex items-center gap-2 px-2 py-1.5 rounded-sm text-xs cursor-pointer border transition-colors ${
                selected.includes(c.id) ? "border-fuchsia-500 bg-fuchsia-500/10" : "border-border hover:bg-muted"
              }`}
              data-testid={`assign-category-${c.id}`}
            >
              <input
                type="checkbox"
                checked={selected.includes(c.id)}
                onChange={() => toggle(c.id)}
                className="sr-only"
              />
              <span className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: c.color }} />
              <span className="truncate">{c.name}</span>
            </label>
          ))}
        </div>
        <div className="py-1">
          <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Channels (targeting)</label>
          <p className="text-[11px] text-muted-foreground mb-1.5">Leave empty to play this clip on all channels.</p>
          <div className="flex flex-wrap gap-1.5 max-h-28 overflow-auto" data-testid="clip-channels-multiselect">
            {channels.map((c) => {
              const sel = selChannels.includes(c.id);
              return (
                <button
                  type="button"
                  key={c.id}
                  data-testid={`clip-channel-${c.id}`}
                  onClick={() => toggleChannel(c.id)}
                  className={`px-2 py-1 rounded-sm text-xs border transition-colors ${sel ? "border-fuchsia-500 bg-fuchsia-500/10 text-foreground" : "border-border text-muted-foreground hover:bg-muted"}`}
                >
                  {c.name}
                </button>
              );
            })}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} data-testid="assign-categories-save">
            Save {selected.length ? `(${selected.length})` : ""}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
